import { Component, Input, Output, EventEmitter, forwardRef, OnInit, HostListener } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SearchableSelectOption {
  value: any;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-searchable-select',
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableSelectComponent),
      multi: true
    }
  ]
})
export class SearchableSelectComponent implements OnInit, ControlValueAccessor {
  @Input() options: SearchableSelectOption[] = [];
  @Input() placeholder: string = 'Select an option';
  @Input() clearable: boolean = true;
  @Input() disabled: boolean = false;
  @Input() size: 'sm' | 'md' | 'lg' = 'sm';
  @Input() searchPlaceholder: string = 'Search...';
  @Input() noResultsText: string = 'No results found';
  @Input() clearText: string = 'Clear selection';

  @Output() selectionChange = new EventEmitter<any>();

  selectedValue: any = null;
  selectedOption: SearchableSelectOption | null = null;
  searchTerm: string = '';
  isOpen: boolean = false;
  filteredOptions: SearchableSelectOption[] = [];

  private onChange = (value: any) => {};
  private onTouched = () => {};

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const dropdown = target.closest('.searchable-select');
    if (!dropdown) {
      this.closeDropdown();
    }
  }

  ngOnInit(): void {
    this.filteredOptions = [...this.options];
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    this.selectedValue = value;
    this.selectedOption = this.options.find(option => option.value === value) || null;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggleDropdown(): void {
    if (this.disabled) return;
    
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.searchTerm = '';
      this.filteredOptions = [...this.options];
      setTimeout(() => {
        const searchInput = document.querySelector('.searchable-select-search') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
      }, 100);
    }
    this.onTouched();
  }

  closeDropdown(): void {
    this.isOpen = false;
  }

  onSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value.toLowerCase();
    this.filterOptions();
  }

  private filterOptions(): void {
    if (!this.searchTerm) {
      this.filteredOptions = [...this.options];
    } else {
      this.filteredOptions = this.options.filter(option =>
        option.label.toLowerCase().includes(this.searchTerm)
      );
    }
  }

  selectOption(option: SearchableSelectOption): void {
    if (option.disabled) return;
    
    this.selectedValue = option.value;
    this.selectedOption = option;
    this.onChange(option.value);
    this.selectionChange.emit(option.value);
    this.closeDropdown();
  }

  clearSelection(): void {
    this.selectedValue = null;
    this.selectedOption = null;
    this.onChange(null);
    this.selectionChange.emit(null);
  }

  getDisplayText(): string {
    return this.selectedOption ? this.selectedOption.label : this.placeholder;
  }

  getSizeClass(): string {
    switch (this.size) {
      case 'sm': return 'form-control-sm';
      case 'lg': return 'form-control-lg';
      default: return '';
    }
  }

  trackByValue(index: number, option: SearchableSelectOption): any {
    return option.value;
  }
}
